/**
 * Shared step definitions for every guided tour in the app.
 *
 * Each tour is an array of step objects:
 *   {
 *     selector?:  CSS selector of the element to spotlight (optional)
 *     placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right' | 'center'
 *     title:      Short headline (uppercase looks best)
 *     body:       1–2 sentence explanation
 *     accentColor?: hex for the title color (defaults to burnt orange)
 *   }
 *
 * If `selector` misses or `placement` is "center", the tooltip is centered
 * on the viewport with a dim backdrop instead of a spotlight cutout.
 */

export const DASHBOARD_TUTORIAL = [
  {
    placement: "center",
    title: "Welcome to your dashboard",
    body: "This is the command center for your entire job hunt. Resmo organizes everything into Ingredients (what you feed it), Utensils (tools you can grab), and The Meal (what Resmo discovered about you).",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="phase-progress-bar"]',
    placement: "bottom",
    title: "Track your progress",
    body: "Each phase lights up as you complete it. Click any segment to jump straight to that card.",
    accentColor: "#CA8A04",
  },
  {
    selector: '[data-testid="ingredients-heading"]',
    placement: "bottom",
    title: "The Ingredients",
    body: "Five phases to feed Resmo: upload a resume, tailor a job description, build your network, prep for interviews, and reflect afterwards.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="utensils-heading"]',
    placement: "top",
    title: "The Utensils",
    body: "Skills Lab, Role Research, Live Job Search, Mock Interviews and more — grab any tool whenever you need it.",
    accentColor: "#20C1A7",
  },
  {
    selector: '[data-testid="meal-heading"]',
    placement: "left",
    title: "The Meal",
    body: "On the right, Resmo serves up your Superpower, Personalized Insights, the latest Resume Makeover, Interview Test Drive, and Stepping Stones reflections.",
    accentColor: "#1E40AF",
  },
];

export const RESUME_TUTORIAL = [
  {
    placement: "center",
    title: "Feed Resmo your resume",
    body: "You can upload a PDF/DOCX, paste plain text, or build one from scratch with the 6-step wizard. Resmo only needs this once — it remembers everything.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="resume-upload-btn"], [data-testid="hero-add-resume"], [data-testid="hero-replace-resume"]',
    placement: "bottom",
    title: "Upload",
    body: "Drop a PDF or DOCX (max 5MB). Resmo extracts the text and stores it securely.",
    accentColor: "#CA8A04",
  },
  {
    selector: '[data-testid="open-resume-builder-btn"], [data-testid="resume-card-build-from-scratch"]',
    placement: "top",
    title: "Or build from scratch",
    body: "No resume yet? The 6-step builder walks you through identity → summary → experience → education → skills → review.",
    accentColor: "#20C1A7",
  },
  {
    placement: "center",
    title: "Then what?",
    body: "Once your base resume is in, every other Resmo feature unlocks: job description tailoring, ATS scoring, mock interviews, the works.",
    accentColor: "#1E40AF",
  },
];

export const JD_TUTORIAL = [
  {
    placement: "center",
    title: "Tailor to a job description",
    body: "Paste a job description (or upload a file). Resmo extracts the role's real problem, scores your fit, rewrites your resume, drafts a cover letter, and pulls company intel.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="feed-job-textarea"], textarea',
    placement: "top",
    title: "Paste the Job Description",
    body: "Paste the full job description. The more detail, the more accurate the tailored output.",
    accentColor: "#CA8A04",
  },
  {
    selector: '[data-testid="feed-job-submit-btn"], [data-testid="bulk-tailor-submit-btn"]',
    placement: "top",
    title: "Submit & wait",
    body: "Resmo will chew on it for ~30-90 seconds. You'll be redirected to your tailored output when it's ready.",
    accentColor: "#20C1A7",
  },
  {
    placement: "center",
    title: "Bulk mode too",
    body: "Got multiple job descriptions to tailor at once? Switch to Bulk Mode and queue up to 5 at a time. Resmo emails you when they're all ready.",
    accentColor: "#1E40AF",
  },
];

export const SKILLS_TUTORIAL = [
  {
    placement: "center",
    title: "Skills Lab · Salary Assessment",
    body: "Resmo extracts your skills from your resume, lets you rate them 1-5, and returns a realistic salary range + adjacent roles you'd be 90% qualified for.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="skills-extract-btn"], [data-testid="skills-add-skill-btn"]',
    placement: "bottom",
    title: "Extract or add skills",
    body: "Resmo auto-extracts skills from your base resume. Add or remove any, then rate each 1-5.",
    accentColor: "#CA8A04",
  },
  {
    selector: '[data-testid="skills-submit-assessment-btn"]',
    placement: "top",
    title: "Get your assessment",
    body: "Resmo crunches your skills against current market data and gives you a salary range, adjacent roles, and negotiation talking points.",
    accentColor: "#20C1A7",
  },
];

export const NETWORK_TUTORIAL = [
  {
    placement: "center",
    title: "Build your stepping stones",
    body: "Networking ≠ spam. Resmo helps you build an elevator pitch, log real contacts, generate authentic conversation sparks, coach your mindset, and find the right rooms.",
    accentColor: "#0E5A4A",
  },
  {
    selector: '[data-testid="people-panel"], [data-testid="add-contact-btn"]',
    placement: "bottom",
    title: "Add stepping stones",
    body: "Add 3+ contacts manually, or import your LinkedIn CSV. Each becomes a 'stone' on your career path.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="sparks-generate-btn"], [data-testid="sparks-panel"]',
    placement: "top",
    title: "Conversation Sparks",
    body: "For any contact, generate 5 openers, 5 deeper questions, and 5 reciprocity moves. Authentic, never spammy.",
    accentColor: "#FFC145",
  },
  {
    selector: '[data-testid="events-panel"], [data-testid="events-submit-btn"]',
    placement: "top",
    title: "Find the rooms",
    body: "Resmo suggests real communities + search queries to discover events in your space. Show up where it matters.",
    accentColor: "#1E40AF",
  },
];

export const STUDIO_TUTORIAL = [
  {
    placement: "center",
    title: "Resume Studio · live ATS scoring",
    body: "The Studio shows your tailored resume side-by-side with a live ATS score, AI suggestions ranked by impact, and 6 ATS-friendly templates.",
    accentColor: "#FF6A00",
  },
  {
    selector: '[data-testid="ats-score-badge"]',
    placement: "right",
    title: "ATS Score",
    body: "Resmo grades your resume against the job description on a 0-100 scale. Hit 60+ to unlock PDF/DOCX downloads.",
    accentColor: "#CA8A04",
  },
  {
    selector: '[data-testid="studio-templates"]',
    placement: "left",
    title: "Templates",
    body: "Pick from 6 ATS-friendly layouts. Each shows a sample preview so you know exactly what you're picking.",
    accentColor: "#20C1A7",
  },
  {
    selector: '[data-testid="share-template-card"]',
    placement: "left",
    title: "Share a winning template",
    body: "Got a layout that landed you interviews? Submit it to Resmo — we'll consider adding it to the gallery (credit to you).",
    accentColor: "#B45309",
  },
];

export const TUTORIALS = {
  dashboard: DASHBOARD_TUTORIAL,
  resume:    RESUME_TUTORIAL,
  jd:        JD_TUTORIAL,
  skills:    SKILLS_TUTORIAL,
  network:   NETWORK_TUTORIAL,
  studio:    STUDIO_TUTORIAL,
};
